import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';

import { contentTypesList } from '@/shared/api/generated/content/content';
import type {
  EnvironmentBody,
  ShopSettingsBody,
  SiteDetailBody,
  TypeBody,
} from '@/shared/api/generated/model';
import { shopGet } from '@/shared/api/generated/shop/shop';
import { sitesEnvironments, sitesGet } from '@/shared/api/generated/sites/sites';

import {
  allows,
  allowsShared,
  kinds,
  environmentFor,
  isEnvKey,
  type EnvKey,
  type Permission,
  type SiteRole,
} from './scope';

export interface SiteScope {
  siteId: string;
  env: EnvKey;
  site: SiteDetailBody | undefined;
  role: SiteRole | undefined;
  /** Сужение «только Development»: Production человеку не виден. */
  devOnly: boolean;
  /** Разрешение на содержимое текущего окружения. */
  can: (permission: Permission) => boolean;
  /** Разрешение на то, что общее для обоих окружений (устройство, медиа). */
  canShared: (permission: Permission) => boolean;
  environments: EnvironmentBody[];
  /** Окружение из адреса; undefined — пока не загрузилось или его у сайта нет. */
  environment: EnvironmentBody | undefined;
  /** Коллекции и одиночки окружения в порядке, заданном в «Моделях». Блоков
   *  здесь нет: своих записей у них нет, и в меню содержимого им не место. */
  types: TypeBody[];
  /** Пак «Магазин» окружения (фаза 9); undefined — не включён или не загрузился. */
  shop: ShopSettingsBody | undefined;
  loading: boolean;
}

/**
 * Сайт и окружение, в которых человек сейчас находится, — из адреса.
 *
 * Вне сайта отдаёт null: оболочка по этому признаку решает, какое меню
 * показывать. Ключи запросов совпадают с теми, что у экранов сайта, — сайт,
 * окружения и типы запрашиваются один раз на всю оболочку, а не каждым
 * экраном заново.
 */
export function useSiteScope(): SiteScope | null {
  const params: { siteId?: string; env?: string } = useParams({ strict: false });
  const siteId = params.siteId ?? '';
  const env = params.env !== undefined && isEnvKey(params.env) ? params.env : undefined;
  const inside = siteId !== '' && env !== undefined;

  const site = useQuery({
    queryKey: ['site', siteId],
    enabled: inside,
    queryFn: async () => {
      const response = await sitesGet(siteId);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос сайта');
      }

      return response.data;
    },
  });

  const environments = useQuery({
    queryKey: ['environments', siteId],
    enabled: inside,
    queryFn: async () => {
      const response = await sitesEnvironments(siteId);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос окружений');
      }

      return response.data.items ?? [];
    },
  });

  const role = site.data?.my_role;
  const devOnly = site.data?.dev_only ?? false;

  // Типы — только тем, кто читает содержимое: у поддержки этот запрос
  // закончился бы отказом, а меню — ошибкой вместо пустоты.
  const types = useQuery({
    // Схема у каждого окружения своя — и меню у каждого своё.
    queryKey: ['content-types', siteId, env],
    enabled: inside && env !== undefined && allows(role, 'content.read'),
    queryFn: async () => {
      const response = await contentTypesList(siteId, kinds[env ?? 'prod'], {});

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос типов контента');
      }

      return response.data.items ?? [];
    },
  });

  // Магазин — тем же, кто читает содержимое: меню показывает «Магазин», а
  // формы — без заказов.
  const shop = useQuery({
    queryKey: ['shop', siteId, env],
    enabled: inside && env !== undefined && allows(role, 'content.read'),
    queryFn: async () => {
      const response = await shopGet(siteId, kinds[env ?? 'prod']);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос магазина');
      }

      return response.data;
    },
  });

  if (!inside) {
    return null;
  }

  const envList = environments.data ?? [];

  return {
    siteId,
    env,
    site: site.data,
    role,
    devOnly,
    can: (permission) => allows(role, permission),
    canShared: (permission) => allowsShared(role, devOnly, permission),
    environments: envList,
    environment: environmentFor(envList, env),
    types: (types.data ?? [])
      .filter((item) => item.kind !== 'block')
      .sort((a, b) => a.position - b.position),
    shop: shop.data?.enabled === true ? shop.data : undefined,
    loading: site.isLoading || environments.isLoading || types.isLoading,
  };
}
